import { useRef, useEffect } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import Typography from 'src/components/Typography';
import CheckIcon from 'src/components/Icons/CheckIcon';
import {
  trackNewSignUpNavCredentialsClick,
  trackNewSignUpNavProfessionalInfoClick,
} from 'src/services/tracking/events/signup.events';

export enum NEW_TABS_INDEX {
  ACCESS_DATA = 0,
  PROFESSIONAL_AND_PERSONAL_DATA = 1,
}

interface TabButtonProps {
  isSelected: boolean;
  isCompleted: boolean;
  onClick: () => void;
  label: string;
}

function TabButton({ isSelected, isCompleted, onClick, label }: TabButtonProps) {
  return (
    <Flex
      as="button"
      type="button"
      onClick={onClick}
      align="center"
      justify="center"
      gap="8px"
      px="8px"
      py="8px"
      w="50%"
      borderRadius="15px"
      bg={isSelected ? 'secondary.100' : 'transparent'}
      transition="all 0.2s ease"
      _hover={{ bg: isSelected ? 'secondary.200' : 'secondary.50' }}
      flexShrink={0}
    >
      <Box flexShrink={0}>
        <CheckIcon width={16} variantColor="secondary.main" />
      </Box>
      <Typography model="caption" weight="bold" color="primary.1000" whiteSpace="nowrap" fontSize={11}>
        {label}
      </Typography>
    </Flex>
  );
}

interface NewTabsHeaderProps {
  currentIndex: NEW_TABS_INDEX;
  isFirstStepCompleted?: boolean;
  onTabClick: (index: NEW_TABS_INDEX) => void;
}

export function NewTabsHeader({ currentIndex, isFirstStepCompleted, onTabClick }: NewTabsHeaderProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    if (currentIndex === NEW_TABS_INDEX.ACCESS_DATA) {
      container.scrollLeft = 0;
    } else {
      container.scrollLeft = 9999;
    }
  }, [currentIndex]);

  const handleTabClick = (index: NEW_TABS_INDEX) => {
    if (index === NEW_TABS_INDEX.ACCESS_DATA) {
      trackNewSignUpNavCredentialsClick();
    } else if (index === NEW_TABS_INDEX.PROFESSIONAL_AND_PERSONAL_DATA) {
      trackNewSignUpNavProfessionalInfoClick();
    }
    onTabClick(index);
  };

  return (
    <Flex
      ref={containerRef}
      bg="surface"
      border="1px solid"
      borderColor={{ base: 'surface', sm: 'primary.50' }}
      py="8px"
      px="10px"
      borderRadius="20px"
      mt="20px"
      mb="30px"
      gap="8px"
      justifyContent="center"
      overflowX="auto"
      overflowY="hidden"
      css={{
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      <TabButton
        isSelected={currentIndex === NEW_TABS_INDEX.ACCESS_DATA}
        isCompleted={!!isFirstStepCompleted}
        onClick={() => handleTabClick(NEW_TABS_INDEX.ACCESS_DATA)}
        label="DADOS DE ACESSO"
      />
      <TabButton
        isSelected={currentIndex === NEW_TABS_INDEX.PROFESSIONAL_AND_PERSONAL_DATA}
        isCompleted={false}
        onClick={() => handleTabClick(NEW_TABS_INDEX.PROFESSIONAL_AND_PERSONAL_DATA)}
        label="DADOS CADASTRAIS"
      />
    </Flex>
  );
}
